import { computed, onMounted, readonly, ref } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useLanguageStore } from '@/helpers/composables/useStores';

import { type MaybeRefOrGetter, toValue } from '@/helpers/composables/utils';

import type { ClickoutUrl } from '@/helpers/clickout-helper';
import type { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';

const sessionStorage = process.client && window != undefined ? window.sessionStorage : null;

// prettier-ignore
const ArbitrationCountries = [
	'AR', 'AT', 'AU', 'BE', 'BR', 'CA', 'CH', 'CL', 'CO', 'CZ', 'DE', 'DK', 'ES', 'FI', 'FR', 'GB', 'IE', 'IL', 'IN', 'IT', 'MX', 'NL', 'NO', 'NZ', 'PH', 'PL', 'PT', 'SA', 'SE', 'US', 'ZA',
] as const;

const CampaignKey = 'utm_campaign' as const;
const ContentKey = 'utm_content' as const;

const _isArbitrating = ref(false);

export const isArbitrating = readonly(_isArbitrating);

export function useArbitrage({ packageId }: { packageId?: number } = {}) {
	const route = useRoute();
	const { country } = useLanguageStore();

	onMounted(() => {
		if (!ArbitrationCountries.includes(country.value)) return;
		if (_isArbitrating.value || process.server) return;

		const campaign = route.query[CampaignKey] as string | null;
		const content = route.query[ContentKey] as string | null;
		if (content == null || campaign == null) return;

		sessionStorage?.setItem(CampaignKey, campaign);
		sessionStorage?.setItem(ContentKey, content);

		_isArbitrating.value = true;
	});

	const clearArbitration = () => {
		sessionStorage?.removeItem(CampaignKey);
		sessionStorage?.removeItem(ContentKey);

		_isArbitrating.value = false;
	};

	const arbitrationValue = computed(() => {
		const content = sessionStorage?.getItem(ContentKey);

		const campaign = sessionStorage?.getItem(CampaignKey);
		if (!content || !campaign) return null;
		return `${content}-${campaign}`;
	});

	/** Sets the 'uct_ct' query on the Offer URL if needed. */
	function withArbitrageTracking(url: ClickoutUrl, offer: TitleOfferFragment) {
		if (packageId == null) {
			console.warn('[useArbitrage]: Cannot add offer tracking without a package id.');
		}

		if (offer.package.packageId === packageId) {
			return url.set('uct_ct', arbitrationValue.value);
		}

		return url;
	}

	function trackTagEvent(offer: MaybeRefOrGetter<TitleOfferFragment | null>) {
		if (!window || !window.dataLayer) return;
		if (!isArbitrating.value) return;

		if (toValue(offer)?.package.packageId === packageId) {
			window.dataLayer.push({
				event: 'interaction',
				// event category
				target: 'clickout',
				// event label
				'target-properties': 'Apple TV Plus',
			});
		}
	}

	return {
		isArbitrating,
		arbitrationValue,
		clearArbitration,
		withArbitrageTracking,
		trackTagEvent,
	};
}
